import { FC, ReactNode, useEffect, useState } from 'react';
import Loading from '../../components/shared/Loading';
import CollectiveAgreement from '../app-supplier-database/collectiveAgreement';
import BtButton from '../bt-button/bt-button';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment';
import { ServiceSyna } from '../../services/service-syna';
import ServiceCollectiveAgreement from '../../services/service-collectiveAgreement';
import { ServiceId06 } from '../../services/service-id06';
import appState from '../../state/AppStateContainer';
import axios from 'axios';
import { NotificationManager } from 'react-notifications';
import serviceSupplierMonitoring, { MonitoredSupplier } from '../../services/service-supplier-monitoring';
import { ReactComponent as MonitoringHeartIcon } from '../../assets/svg/monitoringHeartIcon.svg';
import { RouteComponentProps } from 'react-router-dom';
import { formatDate, formatDateTime, formatAmount } from '../../util';
import { useTranslation } from 'react-i18next';
import styles from './supplier-controls.module.scss';
import serviceCredibility from '../../services/service-credibility';
import { ReactComponent as PdfIcon } from '../../assets/file-icons/pdf.svg';
import { ReactComponent as RefreshIcon } from '../../assets/svg/refreshIcon.svg';

type Params = {};

type RouteTypes = RouteComponentProps<Params, Record<string, unknown>>;

type Props = {
  organizationNumber: string,
  children?: ReactNode,
  hasSyna: boolean,
  hasCreditsafe: boolean,
  className?: string,
  history: RouteTypes['history'],
}

type Id06 = {
  status: boolean,
  cards: number,
  error: null,
}

type Paye = {
  amount: number,
  period: Date
}

type CreditsafeDetails = {
  newDateOfLatestChange?: Date,
  dateOfLatestChange: Date,
  registeredMunicipality: string,
  registeredForFTax: boolean,
  registeredForVat: boolean,
  registeredForEmployeeTax: boolean,
  revenue: {
    currency: string,
    value: number
  },
  employees: number,
  paye: Paye,
  rating: {
    commonValue: string,
    commonDescription: string,
    creditLimit: {
      currency: string,
      value: number
    },
    providerValue: {
      maxValue: number,
      minValue: number,
      value: number
    },
    providerDescription: string,
    pod: number
  }
};

type CreditsafeError = {
  status?: number | null,
  i18nKey: string,
  description: string
}

type SynaDetails = {
  sate: string,
  fskatt: boolean,
  momsreg: boolean,
  arbetsgivarreg: boolean,
  companyType: string | null,
  revenue: string,
  employees: number,
  arbgivavg: { amount: number, year: number, month: number },
  error: null,
  rating: number,
  comment: string,
  postponedTax: { date: string, amount: number }[],
}

type ByggInstSektornAgreement = {
  Medlemsorganisation: string,
  Avtal: string,
  FackligMotpart: string,
}

type CollectiveAgreements = {
  kollektivavtalskollen: {
    source_data: any,
    error: string,
  },
  byggInstSektorn: {
    legalName: string,
    organizationNumber: string,
    collectiveAgreements: ByggInstSektornAgreement[],
    error: string,
  },
}

const YesNo: FC<{ value: boolean }> = ({ value }) => {
  const [t] = useTranslation('supplierDatabase', { keyPrefix: 'supplierControls' });
  return value ? (
    <div className="collective-agreementFlag true">
      <CheckIcon /> {t('label.yes', 'Ja')}
    </div>
  ) : (
    <div className="collective-agreementFlag false">
      <CloseIcon /> {t('label.no', 'Nej')}
    </div>
  )
}

const SupplierControls: FC<Props> = ({ organizationNumber, children, hasSyna, hasCreditsafe, className, history }) => {
  const [t] = useTranslation('supplierDatabase', { keyPrefix: 'supplierControls' });
  const [id06, setId06] = useState<Id06 | null>(null);
  const [agreement, setAgreement] = useState<CollectiveAgreements | null>(null);
  const [synaDetails, setSynaDetails] = useState<SynaDetails | null>(null);
  const [creditsafeDetails, setCreditsafeDetails] = useState<CreditsafeDetails | null>(null);
  const [creditsafeError, setCreditsafeError] = useState<CreditsafeError | null>(null);
  const [paye, setPaye] = useState<Paye | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCreditsafeDetails, setLoadingCreditsafeDetails] = useState(false);
  const [agreementError, setAgreementError] = useState('');
  const [monitoredSupplier, setMonitoredSupplier] = useState<MonitoredSupplier>();
  const synaLoader = false;

  useEffect(() => {
    (async (organizationNumber: string) => {
      setLoading(true);
      setSynaDetails(null);
      setCreditsafeDetails(null);
      setAgreement(null);
      setId06(null);
      setPaye(null);

      if (hasSyna) {
        ServiceSyna.getSynaDetails(organizationNumber).then((data) => {
          setSynaDetails(data);
          if (data.arbgivavg?.amount && data.arbgivavg.year && data.arbgivavg.month) {
            setPaye({
              amount: data.arbgivavg.amount,
              period: new Date(data.arbgivavg.year, data.arbgivavg.month-1)
            });
          }
        });
        serviceSupplierMonitoring.fetchMonitoredSupplier(organizationNumber).then(data => setMonitoredSupplier(data));
      }

      if (hasCreditsafe) {
        await getCachedCreditsafeDetails();
      }

      try {
        const result = (await ServiceCollectiveAgreement.getCollectiveAgreement(organizationNumber)) as CollectiveAgreements;
        if (result) {
          setAgreement(result);
        }
      } catch (e) {
        console.log(e);
        setAgreementError(t('agreementError', 'Kunde inte hämta kollektivavtal.'));
      }

      setLoading(false);
      const id06response = await ServiceId06.checkId06(organizationNumber);
      setId06(id06response.data);
    })(organizationNumber);
  }, [organizationNumber]);

  const getErrorMsg = (err: string) => {
    let error = t('notification.error.description', 'Det gick inte att hämta rapporten');
    const publicMsgIndex = err.indexOf('Public message:');
    const errorCodeIndex = err.indexOf('public error code:');

    if (publicMsgIndex > -1) error = err.slice(publicMsgIndex + 16, errorCodeIndex);
    return error;
  };

  const downloadSyna = async (organizationNumber: string) => {
    const orgSlug = appState.getCurrentOrgSlug();
    const targetWindow = window.open(`/${orgSlug}/syna/report`) as (Window & { fetchingReport: boolean });
    if (targetWindow) {
      targetWindow.fetchingReport = true;
      const res = await axios.get('/api/syna/report', { params: { organizationNumber } });
      targetWindow.fetchingReport = false;
      if (res.data.link) {
        targetWindow.location = res.data.link;
      } else {
        targetWindow.close();
        NotificationManager.error(getErrorMsg(res.data.error), t('notification.error.title', 'Fel vid hämtning av rapport'));
      }
    }
  };

  const renderSynaIndicatorAndButton = (organizationNumber: string) => (synaLoader ? (
    <Loading type="inline" loadingStyle="bubbles" size="30px" />
  ) : (
    <>
      <div className="sp-control-row">
        <b>{t('creditCheck.heading', 'Kreditupplysning')}</b>
        <div>
          { synaDetails ? (
            synaDetails.error ? (
              <span className="indicator">?</span>
            ) : (
              <span className={'indicator ' + { 1: 'high', 2: 'medium', 3: 'low', 4: 'low', 5: 'low' }[synaDetails.rating]}>{synaDetails.rating}</span>
            )
          ) : (
            <Loading type="inline" loadingStyle="bubbles" size="30px" />
          )}
        </div>
        <div>
          <BtButton
            onClick={() => downloadSyna(organizationNumber)}
            color="white"
            size="xxs"
            class="supplierControlsElementToHide"
          >{t('creditCheck.button', 'Syna rapport')}</BtButton>
        </div>
      </div>
      {!!synaDetails?.comment?.trim() && <div className={styles.info}>{synaDetails.comment}</div>}
    </>
  ));

  const renderKeyValue = (key: string, value: ReactNode) => (
    <div className="sp-control-row">
      <b>{key}</b>
      <div>{value}</div>
    </div>
  );

  const renderLoader = () => (
    <div className="ar-row" style={{ justifyContent: 'center', padding: '8px 0 16px 0' }}>
      <Loading type="inline" color="#ff5e15" loadingStyle="bubbles" size="30px" />
    </div>
  );

  const renderSynaDetails = () => {
    if (!hasSyna) return null;
    if (synaDetails) {
      return synaDetails.error ? (
        <div className="ar-row" style={{ justifyContent: 'center', padding: '8px 0 16px 0' }}>
          {t('creditCheck.errorMessage', 'Kunde inte hämta uppgifter från Syna')}
        </div>
       ) : (
        <>
          {renderKeyValue(t('creditCheck.residence', 'Säte'), synaDetails.sate)}
          {renderKeyValue(t('creditCheck.companyType', 'Företagsform'), synaDetails.companyType)}
          {renderKeyValue(t('creditCheck.fskatt', 'F-skatt'), <YesNo value={synaDetails.fskatt} />)}
          {renderKeyValue(t('creditCheck.momsreg', 'Reg. för moms'), <YesNo value={synaDetails.momsreg} />)}
          {renderKeyValue(t('creditCheck.arbetsgivarreg', 'Reg. arbetsgivare'), <YesNo value={synaDetails.arbetsgivarreg} />)}
          {renderKeyValue(t('creditCheck.revenue.title', 'Omsättning'), synaDetails.revenue != null ?
            t('creditCheck.revenue.value', '{{amount}} tkr', { amount: (+synaDetails.revenue).toLocaleString() })
            : '–')}
          {renderKeyValue(t('creditCheck.employees', 'Anställda'), synaDetails.employees != null ? (+synaDetails.employees).toLocaleString() : '–')}
          {renderKeyValue(t('creditCheck.postponedTax.title', 'Skatteanstånd'), synaDetails.postponedTax?.[0]?.amount ?
            t('creditCheck.postponedTax.value', '{{amount}} kr', { amount: (+synaDetails.postponedTax[0].amount).toLocaleString() })
            : '–')}
        </>
       );
    } else {
      return renderLoader();
    }
  }

  const downloadCreditsafeReport = async () => {
    try {
      const resp = await serviceCredibility.downloadCreditsafePdf(organizationNumber);
      window.open(URL.createObjectURL(resp.data), '_blank');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        NotificationManager.error(t('creditCheck.creditSafe.notification.error.pdfNotFound', 'Aktuell pdf-rapport för detta företag saknas.'));
      } else {
        NotificationManager.error(t('creditCheck.creditSafe.notification.error.pdfUnknownError', 'Det gick inte att ladda ner pdf-rapport. Försök igen senare.'));
      }
    }
  };

  const getCachedCreditsafeDetails = async () => {
    setCreditsafeDetails(null);
    setCreditsafeError(null);
    setLoadingCreditsafeDetails(true);
    try {
      const data = await serviceCredibility.getCachedCreditsafeDetails(organizationNumber);
      if (data) {
        setCreditsafeDetails(data);
        if (data.paye) {
          setPaye(data.paye);
        }
      }
    } catch(error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        setCreditsafeError({
          status: error.response.status,
          ...error.response.data.error
        });
      } else {
        setCreditsafeError({ i18nKey: 'unknown', description: 'Could not get Creditsafe data' });
      }
    } finally {
      setLoadingCreditsafeDetails(false);
    }
  };

  const getCreditsafeDetails = async () => {
    setCreditsafeDetails(null);
    setCreditsafeError(null);
    setLoadingCreditsafeDetails(true);
    try {
      const data = await serviceCredibility.getCreditsafeDetails(organizationNumber);
      setCreditsafeDetails(data);
    } catch (error) {
      setCreditsafeError({ i18nKey: 'unknown', description: 'Could not get Creditsafe data' });
//      console.error('Creditsafe error:', error);
    } finally {
      setLoadingCreditsafeDetails(false);
    }
  };

  const renderCreditsafeDetails = () => {
    if (!hasCreditsafe) return null;
    if (creditsafeError) {
      return <>
        <div className="sp-control-row">
          <b>{t('creditCheck.creditsafe.heading', 'Information från Creditsafe')}</b>
          <div>
            <div className={styles['flag']}>
              <CloseIcon className={styles['flag__false']} /> {t('creditCheck.creditsafe.status.error', 'Saknas')}
            </div>
          </div>
        </div>
        <div className={styles.info}>
          { t(`creditCheck.creditsafe.error.${creditsafeError.i18nKey || 'unknown'}`, creditsafeError.description) }
        </div>
        { creditsafeError.status !== 400 && (
          <div className={styles['creditsafe-button']}>
            <BtButton
              onClick={getCreditsafeDetails}
              color="white"
              size="xxs"
              class="supplierControlsElementToHide"
            >{ t('creditCheck.creditsafe.button.retry', 'Försök igen') }</BtButton>
          </div>
        )}
      </>;
    }
    if (creditsafeDetails) {
      return <>
        <div className="sp-control-row">
          <b>{t('creditCheck.creditsafe.heading', 'Information från Creditsafe')}</b>
          <div className={styles['flag']}>
            { creditsafeDetails.newDateOfLatestChange ? <>
              <CloseIcon className={styles['flag__false']} /> {t('creditCheck.creditsafe.status.outofdate', 'Inaktuell')}
            </> : <>
              <CheckIcon className={styles['flag__true']} /> {t('creditCheck.creditsafe.status.uptodate', 'Uppdaterad')}
            </>}
          </div>
        </div>
        {creditsafeDetails.newDateOfLatestChange ? <>
          <div className={styles.info}>
            {t(
              'creditCheck.creditsafe.message.outofdate',
              'Hämtad kreditupplysning är senast ändrad {{oldDate}}, senaste ändringen i Creditsafe är från {{newDate}}.',
              { oldDate: formatDateTime(creditsafeDetails.dateOfLatestChange), newDate: formatDateTime(creditsafeDetails.newDateOfLatestChange) }
            )}
          </div>
          <div className={styles['creditsafe-button']}>
            <BtButton
              onClick={getCreditsafeDetails}
              leftIcon={<RefreshIcon />}
              color="white"
              size="xxs"
              class="supplierControlsElementToHide"
            >{ t('creditCheck.creditsafe.button.update', 'Uppdatera kreditupplysning') }</BtButton>
          </div>
        </> : <>
          <div className={styles.info}>
            {t(
              'creditCheck.creditsafe.message.uptodate',
              'Hämtad kreditupplysning är senast ändrad {{date}} och sedan dess har inget ändrats i Creditsafe.',
              { date: formatDateTime(creditsafeDetails.dateOfLatestChange) }
            )}
          </div>
        </>}
        {renderKeyValue(t('creditCheck.residence', 'Säte'), <span className={styles.residence}>{creditsafeDetails.registeredMunicipality.toLowerCase()}</span>)}
        {renderKeyValue(t('creditCheck.fskatt', 'F-skatt'), <YesNo value={creditsafeDetails.registeredForFTax} />)}
        {renderKeyValue(t('creditCheck.momsreg', 'Reg. för moms'), <YesNo value={creditsafeDetails.registeredForVat} />)}
        {renderKeyValue(t('creditCheck.arbetsgivarreg', 'Reg. arbetsgivare'), <YesNo value={creditsafeDetails.registeredForEmployeeTax} />)}
        {renderKeyValue(t('creditCheck.revenue.title', 'Omsättning'), creditsafeDetails.revenue ? `${formatAmount(creditsafeDetails.revenue.value)} ${creditsafeDetails.revenue.currency}` : '–')}
        {renderKeyValue(t('creditCheck.employees', 'Anställda'), creditsafeDetails.employees ? formatAmount(creditsafeDetails.employees) : '–')}
        {renderKeyValue(t('creditCheck.creditsafe.label.creditRating', 'Kreditvärdighet'), creditsafeDetails.rating?.providerValue ?
          <span className={'indicator ' + { 'E': 'high', 'D': 'medium', 'C': 'low', 'B': 'low', 'A': 'low' }[creditsafeDetails.rating?.commonValue]}>
            {`${creditsafeDetails.rating.providerValue.value}/${creditsafeDetails.rating.providerValue.maxValue}`}
          </span>
          : '–'
        )}
        <div className="sp-control-row">
          <b>{t('creditCheck.heading', 'Kreditupplysning')}</b>
          <div>
            <BtButton
              onClick={downloadCreditsafeReport}
              leftIcon={<PdfIcon width="16px" height="16px" />}
              color="white"
              size="xxs"
              class="supplierControlsElementToHide"
            >{ t('creditCheck.creditsafe.button.showPdf', 'Visa rapport') }</BtButton>
          </div>
        </div>
      </>;
    } else {
      return <>
        <div className="sp-control-row">
          <b>{t('creditCheck.creditsafe.heading', 'Information från Creditsafe')}</b>
          { loadingCreditsafeDetails ? (
            renderLoader()
          ) : (
            <div className={styles['flag']}>
              <CloseIcon className={styles['flag__false']} /> {t('creditCheck.creditsafe.status.missing', 'Ej hämtad')}
            </div>
          )}
        </div>
        { !loadingCreditsafeDetails && (
          <div className={styles['creditsafe-button']}>
            <BtButton
              onClick={getCreditsafeDetails}
              color="white"
              size="xxs"
              class="supplierControlsElementToHide"
            >{ t('creditCheck.creditsafe.button.fetchReport', 'Hämta kreditupplysning') }</BtButton>
          </div>
        )}
      </>;
    }
  }

  const url = appState._getCurrentOrgUrl();

  return (
    <div id="supplier-controls-container" className={`ar-column detail-right-col ${className}`}>
      <h3 className="inner-sec-title">{t('heading', 'Kontroll')}</h3>

      <div className="details-right-inner">
        {renderCreditsafeDetails()}

        {renderSynaDetails()}

        <div className="sp-control-row">
          <b>{t('collectiveAgreement.title', 'Kollektivavtal')}</b>
          <CollectiveAgreement agreementData={agreement} error={agreementError} loading={loading} />
        </div>

        {hasSyna && renderSynaIndicatorAndButton(organizationNumber)}

        {id06 && (
          id06.error ? (
            <div className="sp-control-row">
              <b>{t('id06.title', 'ID06')}</b>
              <b>{id06.error == 'Invalid org.nr' ? t('id06.error.invalidOrgNo', 'Ogiltigt organisationsnummer') : t('id06.error.generic', 'Fel vid hämtning från ID06: {{error}}', { error: id06.error })}</b>
            </div>
          ) : (
            <>
              <div className="sp-control-row">
                <b>{t('id06.title', 'ID06')}</b>
                <div key="indicator">
                  <YesNo value={id06.status} />
                </div>
                <div>{t('id06.cards', '{{count}} kort', { count: id06.cards ?? 0 })}</div>
              </div>
              {paye && (
                <>
                  <div className="sp-control-row">
                    <b>{t('id06.arbgivavg.title', 'Arbetsgivaravgifter (för {{period}})', { period: moment(paye.period).locale('sv').format('MMMM YYYY') })}</b>
                    <div>{t('id06.arbgivavg.value', '{{amount}} kr', { amount: formatAmount(paye.amount) })}</div>
                  </div>
                  <div className="sp-control-row">
                    <b>{t('id06.employees.title', 'Antal heltidsanställda (uppskattat)')}</b>
                    <div>≤ {Math.ceil(paye.amount / 0.3142 / 30000)}</div>
                  </div>
                  <div className={styles.info}>{t('id06.employees.info', 'Baserat på 31,42% arbetsgivaravgift och minst 30 000 kr i månadslön')}</div>
                </>
              )}
            </>
          )
        )}

        {hasSyna && (
          <>
            <div className="sp-control-row">
              <b>{t('monitored.heading', 'Bevakad')}</b>
              <YesNo value={!!monitoredSupplier} />
              {monitoredSupplier && history && (
                <div className='supplierControlsElementToHide'>
                  <BtButton
                    size="xxs"
                    color="white"
                    leftIcon={<MonitoringHeartIcon />}
                    onClick={() => {
                      history.push(`${url}/supplier-monitoring/events`, {
                        supplier: {
                          name: monitoredSupplier.name,
                          organizationNumber: monitoredSupplier.organizationNumber,
                        },
                      })
                    }}
                  >
                    {t('monitored.button.showEvents', 'Visa händelser')}
                  </BtButton>
                </div>
              )}
            </div>
            {monitoredSupplier && (
              <div className={styles.info}>
                {monitoredSupplier.lastEventDate ?
                  t('monitored.since.withLatestEvent', 'Bevakad sedan {{date}}, senaste händelse {{latestEvent}}', { date: formatDate(monitoredSupplier.monitoredSince), latestEvent: formatDate(monitoredSupplier.lastEventDate) })
                  : t('monitored.since.withoutLatestEvent', 'Bevakad sedan {{date}}', { date: formatDate(monitoredSupplier.monitoredSince) })}
              </div>
            )}
          </>
        )}
        {children}
      </div>
    </div>
  );
}

export default SupplierControls;
